import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { login as loginApi } from '../api/auth';
import { getMyMenus } from '../api/menu';
import { NAV_ITEMS } from '../config/navItems';

const AuthContext = createContext(null);

function readSavedAuth() {
  const saved = localStorage.getItem('onetak_admin_auth');
  return saved ? JSON.parse(saved) : null;
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readSavedAuth);
  // 로그인한 역할이 접근 가능한 메뉴(menuUrl) 집합. Sidebar(GNB 노출)와 AdminLayout(화면 단위
  // 접근 제어)이 같은 값을 공유해 "메뉴에는 안 보이지만 주소를 직접 입력하면 열리는" 불일치를
  // 없앤다 - 역할 경계 정리 작업의 일부.
  //   null      : 아직 조회 전/조회 중 (권한 확인 전에는 화면을 그리지 않는다 - fail-safe)
  //   'ALL'     : 조회 실패(네트워크 오류 등) - 서버(SecurityConfig)가 실제 접근 제어를 이미
  //               강제하므로, 조회만 실패했을 때는 화면 자체를 막지 않고 전체 노출로 폴백한다.
  //   Set<str>  : 정상 조회된 허용 menuUrl 목록
  const [allowedMenuUrls, setAllowedMenuUrls] = useState(null);

  useEffect(() => {
    if (!auth) {
      setAllowedMenuUrls(null);
      return undefined;
    }
    let cancelled = false;
    setAllowedMenuUrls(null);
    getMyMenus()
      .then((res) => {
        if (cancelled) return;
        setAllowedMenuUrls(new Set((res.data || []).map((m) => m.menuUrl)));
      })
      .catch(() => {
        if (!cancelled) setAllowedMenuUrls('ALL');
      });
    return () => { cancelled = true; };
  }, [auth?.roleCode]);

  const login = useCallback(async (adminAccount, adminPassword) => {
    const res = await loginApi(adminAccount, adminPassword);
    if (!res.success) {
      throw new Error(res.message || '로그인에 실패했습니다.');
    }
    setAuth(res.data);
    localStorage.setItem('onetak_admin_auth', JSON.stringify(res.data));
    return res.data;
  }, []);

  const logout = useCallback(() => {
    setAuth(null);
    setAllowedMenuUrls(null);
    localStorage.removeItem('onetak_admin_auth');
  }, []);

  /** menuUrl 이 없으면(공용 화면) 항상 true, 조회 중/실패 시에도 화면이 무한 로딩·오탐 차단되지
   * 않도록 관대하게 판단한다 - 실제 차단은 서버가 최종적으로 담당한다. */
  const canAccess = useCallback((menuUrl) => {
    if (!menuUrl) return true;
    if (allowedMenuUrls === 'ALL') return true;
    if (allowedMenuUrls === null) return null; // 아직 모름
    return allowedMenuUrls.has(menuUrl);
  }, [allowedMenuUrls]);

  return (
    <AuthContext.Provider value={{
      auth, login, logout, isAuthenticated: !!auth,
      allowedMenuUrls, canAccess, navItems: NAV_ITEMS,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
